import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiFolder,
  FiFile,
  FiPlus,
  FiFolderPlus,
  FiUpload,
  FiSearch,
  FiTrash2,
  FiDownload,
  FiArrowLeft,
  FiHome,
  FiBriefcase,
  FiEdit2 as IconEdit,
  FiFilter,
  FiEye,
  FiMapPin,
  FiUser,
  FiChevronDown,
  FiActivity
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  PageComponent,
  Button,
  Input,
  Modal,
  Select,
  MultiSelect,
  Card
} from '../../components';
import SendSpecificDocumentModal from './SendSpecificDocumentModal';
import '../CompanyManager/style.css';
import './style.css';

export default function FileManager() {
  const navigate = useNavigate();
  const permissions = usePermission('management');
  const location = useLocation();
  const [companies, setCompanies] = useState([]);

  const [selectedCompanyIdForCreation, setSelectedCompanyIdForCreation] = useState('');
  const [companyFilterIds, setCompanyFilterIds] = useState(['all']);
  
  // Data State
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]); 
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [serviceStations, setServiceStations] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Modals State
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'folder'|'file', id, name }
  
  // Form State
  const [newFolderName, setNewFolderName] = useState('');
  const [linkType, setLinkType] = useState('internal'); // internal, customer, supplier, accountant, service_station, employee
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedAccountants, setSelectedAccountants] = useState([]);
  const [selectedServiceStations, setSelectedServiceStations] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [documentType, setDocumentType] = useState('Arquivo Livre');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragTargetId, setDragTargetId] = useState(null);

  // Filters State
  const [filterEntityType, setFilterEntityType] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('name_asc');

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [renameName, setRenameName] = useState('');
  
  // Upload Modal Extra State
  const [showUploadRecipientsModal, setShowUploadRecipientsModal] = useState(false);
  const [recipientType, setRecipientType] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [createMonthFolder, setCreateMonthFolder] = useState(false);
  
  // Dropdown State
  const [isDeliveryDropdownOpen, setIsDeliveryDropdownOpen] = useState(false);
  const deliveryDropdownRef = useRef(null);
  const [isSpecificDocumentModalOpen, setIsSpecificDocumentModalOpen] = useState(false);

  // Constants
  const ALL_CUSTOMERS_VALUE = 'ALL_CUSTOMERS';
  const ALL_SUPPLIERS_VALUE = 'ALL_SUPPLIERS';
  const ALL_ACCOUNTANTS_VALUE = 'ALL_ACCOUNTANTS';
  const ALL_SERVICE_STATIONS_VALUE = 'ALL_SERVICE_STATIONS';
  const ALL_EMPLOYEES_VALUE = 'ALL_EMPLOYEES';

  // Derived State
  const customerOptions = useMemo(() => {
    return [
      { value: ALL_CUSTOMERS_VALUE, label: 'TODOS' },
      ...customers.map(c => ({ value: c.id, label: c.name || 'Sem Nome' }))
    ];
  }, [customers]);

  const supplierOptions = useMemo(() => {
    return [
      { value: ALL_SUPPLIERS_VALUE, label: 'TODOS' },
      ...suppliers.map(s => ({ value: s.id, label: s.name || s.trade_name || 'Sem Nome' }))
    ];
  }, [suppliers]);

  const accountantOptions = useMemo(() => {
    return [
      { value: ALL_ACCOUNTANTS_VALUE, label: 'TODOS' },
      ...accountants.map(a => ({ value: a.id, label: a.name || a.trade_name || 'Sem Nome' }))
    ];
  }, [accountants]);

  const serviceStationOptions = useMemo(() => {
    return [
      { value: ALL_SERVICE_STATIONS_VALUE, label: 'TODOS' },
      ...serviceStations.map(s => ({ value: s.id, label: s.name || 'Sem Nome' }))
    ];
  }, [serviceStations]);

  const employeeOptions = useMemo(() => {
    return [
      { value: ALL_EMPLOYEES_VALUE, label: 'TODOS' },
      ...employees.map(e => ({ value: e.id, label: e.name || 'Sem Nome' }))
    ];
  }, [employees]);

  const uploadRecipientOptions = useMemo(() => {
      if (!recipientType) return [];
      switch (recipientType) {
          case 'customer': return customerOptions;
          case 'supplier': return supplierOptions;
          case 'accountant': return accountantOptions;
          case 'service_station': return serviceStationOptions;
          case 'employee': return employeeOptions;
          default: return [];
      }
  }, [recipientType, customerOptions, supplierOptions, accountantOptions, serviceStationOptions, employeeOptions]);

  const selectedUploadRecipientsDetails = useMemo(() => {
    let allItems = [];
    let allValue = '';
    
    switch (recipientType) {
        case 'customer': 
            allItems = customers; 
            allValue = ALL_CUSTOMERS_VALUE; 
            break;
        case 'supplier': 
            allItems = suppliers; 
            allValue = ALL_SUPPLIERS_VALUE; 
            break;
        case 'accountant': 
            allItems = accountants; 
            allValue = ALL_ACCOUNTANTS_VALUE; 
            break;
        case 'service_station': 
            allItems = serviceStations; 
            allValue = ALL_SERVICE_STATIONS_VALUE; 
            break;
        case 'employee': 
            allItems = employees; 
            allValue = ALL_EMPLOYEES_VALUE; 
            break;
        default: return [];
    }
    
    if (selectedRecipients.includes(allValue)) {
        return allItems;
    }
    return allItems.filter(i => selectedRecipients.includes(String(i.id)));
  }, [recipientType, selectedRecipients, customers, suppliers, accountants, serviceStations, employees]);

  // Generic helper for "Select All" logic
  function handleMultiSelectChange(newValues, currentSelected, allValue, allItems, setter) {
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);
    
    if (isAllSelected && !wasAllSelected) {
      setter([allValue, ...allItems.map(i => String(i.id))]);
      return;
    }

    if (!isAllSelected && wasAllSelected) {
      setter([]);
      return;
    }

    if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      setter(remaining);
      return;
    }

    const specificIds = newValues.filter(v => v !== allValue);
    if (specificIds.length === allItems.length && allItems.length > 0) {
       setter([allValue, ...specificIds]);
    } else {
       setter(specificIds);
    }
  }

  function handleCustomerSelectChange(newValues) {
    handleMultiSelectChange(newValues, selectedCustomers, ALL_CUSTOMERS_VALUE, customers, setSelectedCustomers);
  }

  function handleSupplierSelectChange(newValues) {
    handleMultiSelectChange(newValues, selectedSuppliers, ALL_SUPPLIERS_VALUE, suppliers, setSelectedSuppliers);
  }

  function handleAccountantSelectChange(newValues) {
    handleMultiSelectChange(newValues, selectedAccountants, ALL_ACCOUNTANTS_VALUE, accountants, setSelectedAccountants);
  }

  function handleServiceStationSelectChange(newValues) {
    handleMultiSelectChange(newValues, selectedServiceStations, ALL_SERVICE_STATIONS_VALUE, serviceStations, setSelectedServiceStations);
  }

  function handleEmployeeSelectChange(newValues) {
    handleMultiSelectChange(newValues, selectedEmployees, ALL_EMPLOYEES_VALUE, employees, setSelectedEmployees);
  }

  function handleUploadRecipientSelectChange(newValues) {
    let allItems = [];
    let allValue = '';
    
    switch (recipientType) {
        case 'customer': 
            allItems = customers; 
            allValue = ALL_CUSTOMERS_VALUE; 
            break;
        case 'supplier': 
            allItems = suppliers; 
            allValue = ALL_SUPPLIERS_VALUE; 
            break;
        case 'accountant': 
            allItems = accountants; 
            allValue = ALL_ACCOUNTANTS_VALUE; 
            break;
        case 'service_station': 
            allItems = serviceStations; 
            allValue = ALL_SERVICE_STATIONS_VALUE; 
            break;
        case 'employee': 
            allItems = employees; 
            allValue = ALL_EMPLOYEES_VALUE; 
            break;
        default: return;
    }
    
    handleMultiSelectChange(newValues, selectedRecipients, allValue, allItems, setSelectedRecipients);
  }

  // Load dependencies when selection for creation changes
  useEffect(() => {
    const companyIdToLoad = (selectedCompanyIdForCreation && selectedCompanyIdForCreation !== 'all') 
      ? selectedCompanyIdForCreation 
      : 'all';

    loadCustomers(companyIdToLoad);
    loadSuppliers(companyIdToLoad);
    loadAccountants(companyIdToLoad);
    loadServiceStations(companyIdToLoad);
    loadEmployees(companyIdToLoad);
  }, [selectedCompanyIdForCreation]);

  // Initialize companies
  useEffect(() => {
    loadCompanies();
  }, []);

  // Click outside handler for dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (deliveryDropdownRef.current && !deliveryDropdownRef.current.contains(event.target)) {
        setIsDeliveryDropdownOpen(false);
      }
    }

    if (isDeliveryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
      return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDeliveryDropdownOpen]);

  async function loadCompanies() {
    try {
      const response = await Api.getCompanies();
      if (Array.isArray(response)) {
        setCompanies(response);
      }
    } catch (error) {
      console.error('Failed to load companies', error);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterIds;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterIds(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyIdForCreation(idsWithoutAll[0]);
    } else {
      setSelectedCompanyIdForCreation('');
    }

    setCurrentFolder(null);
    setBreadcrumbs([]);
  }

  // Load Content
  useEffect(() => {
    if (companies.length > 0) {
      loadContent();
    }
  }, [companyFilterIds, currentFolder, companies]);

  async function loadCustomers(companyId) {
    try {
      const response = await Api.getCustomers(companyId);
      if (Array.isArray(response)) {
        setCustomers(response);
      }
    } catch (error) {
      console.error('Failed to load customers', error);
    }
  }

  async function loadSuppliers(companyId) {
    try {
      const response = await Api.getSuppliers(companyId);
      if (Array.isArray(response)) {
        setSuppliers(response);
      }
    } catch (error) {
      console.error('Failed to load suppliers', error);
    }
  }

  async function loadAccountants(companyId) {
    try {
      const response = await Api.getAccountants(companyId);
      if (Array.isArray(response)) {
        setAccountants(response);
      }
    } catch (error) {
      console.error('Failed to load accountants', error);
    }
  }

  async function loadServiceStations(companyId) {
    try {
      const response = await Api.getServiceStations(companyId);
      if (Array.isArray(response)) {
        setServiceStations(response);
      }
    } catch (error) {
      console.error('Failed to load service stations', error);
    }
  }

  async function loadEmployees(companyId) {
    try {
      const response = await Api.getEmployees(companyId);
      if (Array.isArray(response)) {
        setEmployees(response);
      }
    } catch (error) {
      console.error('Failed to load employees', error);
    }
  }

  async function loadContent() {
    if (companies.length === 0) return;

    setLoading(true);
    try {
      const folderId = currentFolder ? currentFolder.id : null;
      
      let foldersData = [];
      let filesData = [];

      if (folderId) {
        const targetCompanyId = currentFolder.company_id;
        if (targetCompanyId) {
          [foldersData, filesData] = await Promise.all([
            Api.getFolders(targetCompanyId, folderId),
            Api.getFiles(folderId, targetCompanyId)
          ]);
        }
      } else {
        const allValue = 'all';
        const hasAllSelected = companyFilterIds.includes(allValue);
        const selectedIds = companyFilterIds.filter(id => id !== allValue);

        let targetCompanies;

        if (hasAllSelected || selectedIds.length === 0) {
          targetCompanies = companies;
        } else {
          targetCompanies = companies.filter(c => selectedIds.includes(String(c.id)));
        }

        if (!targetCompanies || targetCompanies.length === 0) {
          const promises = companies.map(async c => {
            try {
              const [f, fi] = await Promise.all([
                Api.getFolders(c.id, null),
                Api.getFiles(null, c.id)
              ]);
              return { folders: f || [], files: fi || [] };
            } catch (e) {
              return { folders: [], files: [] };
            }
          });
          const results = await Promise.all(promises);
          foldersData = results.flatMap(r => r.folders);
          filesData = results.flatMap(r => r.files);
        } else if (targetCompanies.length === 1) {
          const companyId = targetCompanies[0].id;
          [foldersData, filesData] = await Promise.all([
            Api.getFolders(companyId, null),
            Api.getFiles(null, companyId)
          ]);
        } else {
          const promises = targetCompanies.map(async c => {
            try {
              const [f, fi] = await Promise.all([
                Api.getFolders(c.id, null),
                Api.getFiles(null, c.id)
              ]);
              return { folders: f || [], files: fi || [] };
            } catch (e) {
              return { folders: [], files: [] };
            }
          });
          const results = await Promise.all(promises);
          foldersData = results.flatMap(r => r.folders);
          filesData = results.flatMap(r => r.files);
        }
      }

      setFolders(Array.isArray(foldersData) ? foldersData : []);
      setFiles(Array.isArray(filesData) ? filesData : []);
    } catch (error) {
      toast.error('Erro ao carregar arquivos.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Restore navigation from Hash
  useEffect(() => {
    async function restoreFromHash() {
      const hash = location.hash;
      const folderId = hash ? hash.replace('#', '') : null;

      // 1. If at root
      if (!folderId) {
        if (currentFolder) {
            setCurrentFolder(null);
            setBreadcrumbs([]);
        }
        return;
      }

      // 2. Optimization: Check if target is already current
      if (currentFolder && String(currentFolder.id) === String(folderId)) {
        return;
      }

      // 3. Optimization: Check if target is in breadcrumbs (Going Back)
      const breadcrumbIndex = breadcrumbs.findIndex(b => String(b.id) === String(folderId));
      if (breadcrumbIndex !== -1) {
        const newBreadcrumbs = breadcrumbs.slice(0, breadcrumbIndex + 1);
        setBreadcrumbs(newBreadcrumbs);
        setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1]);
        return;
      }

      // 4. Optimization: Check if target is a child of current folder (Going Deeper)
      // Note: 'folders' contains children of 'currentFolder'
      const targetInChildren = folders.find(f => String(f.id) === String(folderId));
      if (targetInChildren) {
         setBreadcrumbs([...breadcrumbs, targetInChildren]);
         setCurrentFolder(targetInChildren);
         return;
      }

      // 5. Fallback: Deep Link / Reload - Fetch path recursively
      setLoading(true);
      try {
        const chain = [];
        let nextId = folderId;
        let iterations = 0;
        
        while (nextId && iterations < 10) {
             const folder = await Api.getFolder(nextId);
             if (!folder) break;
             chain.unshift(folder);
             nextId = folder.parent_id;
        }
        
        if (chain.length > 0) {
            setBreadcrumbs(chain);
            setCurrentFolder(chain[chain.length - 1]);
        }
      } catch (error) {
        console.error("Error restoring navigation", error);
        navigate(''); 
      } finally {
        setLoading(false);
      }
    }

    if (companies.length > 0) {
      restoreFromHash();
    }
  }, [location.hash, companies, folders]);



  function handleNavigate(folder) {
    navigate(`#${folder.id}`);
    setSearchTerm('');
  }

  function handleNavigateUp() {
    if (breadcrumbs.length === 0) return;
    const parent = breadcrumbs[breadcrumbs.length - 2];
    if (parent) {
        navigate(`#${parent.id}`);
    } else {
        navigate('');
    }
    setSearchTerm('');
  }

  function handleNavigateBreadcrumb(index) {
    if (index === -1) {
      navigate('');
    } else {
      const folder = breadcrumbs[index];
      navigate(`#${folder.id}`);
    }
    setSearchTerm('');
  }

  async function handleRename() {
    if (!renameName.trim()) {
      toast.warning('Digite o novo nome.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (renameItem.type === 'folder') {
        await Api.updateFolder(renameItem.id, { name: renameName });
      } else {
        await Api.updateFile(renameItem.id, { name: renameName });
      }
      toast.success('Renomeado com sucesso!');
      setIsRenameModalOpen(false);
      setRenameItem(null);
      setRenameName('');
      loadContent();
    } catch (error) {
      toast.error('Erro ao renomear.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function openRenameModal(item) {
    setRenameItem(item);
    setRenameName(item.name);
    setIsRenameModalOpen(true);
  }

  // Action Handlers
  async function handleCreateFolder() {
    if (!selectedCompanyIdForCreation) {
      toast.warning('Selecione uma empresa.');
      return;
    }
    if (!newFolderName.trim()) {
      toast.warning('Digite o nome da pasta.');
      return;
    }

    setIsSubmitting(true);
    try {
      let customersList = [];
      let suppliersList = [];
      let accountantsList = [];
      let serviceStationsList = [];
      let employeesList = [];

      if (linkType === 'customer') {
          // Filter out the "ALL_CUSTOMERS" flag if present
          customersList = selectedCustomers.filter(id => id !== ALL_CUSTOMERS_VALUE);
      } else if (linkType === 'supplier') {
          suppliersList = selectedSuppliers.filter(id => id !== ALL_SUPPLIERS_VALUE);
      } else if (linkType === 'accountant') {
          accountantsList = selectedAccountants.filter(id => id !== ALL_ACCOUNTANTS_VALUE);
      } else if (linkType === 'service_station') {
          serviceStationsList = selectedServiceStations.filter(id => id !== ALL_SERVICE_STATIONS_VALUE);
      } else if (linkType === 'employee') {
          employeesList = selectedEmployees.filter(id => id !== ALL_EMPLOYEES_VALUE);
      }

      await Api.createFolder({
        name: newFolderName,
        company_id: selectedCompanyIdForCreation,
        parent_id: currentFolder ? currentFolder.id : null,
        customers: customersList,
        suppliers: suppliersList,
        accountants: accountantsList,
        service_stations: serviceStationsList,
        employees: employeesList,
        is_public: false
      });
      toast.success('Pasta criada com sucesso!');
      setIsFolderModalOpen(false);
      setNewFolderName('');
      setLinkType('internal');
      setSelectedCustomers([]);
      setSelectedSuppliers([]);
      setSelectedAccountants([]);
      setSelectedServiceStations([]);
      setSelectedEmployees([]);
      loadContent();
    } catch (error) {
      toast.error('Erro ao criar pasta.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUploadFile() {
    if (!selectedCompanyIdForCreation) {
      toast.warning('Selecione uma empresa.');
      return;
    }
    if (!uploadFile) {
      toast.warning('Selecione um arquivo.');
      return;
    }

    setIsSubmitting(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(uploadFile);
      reader.onload = async () => {
        const base64 = reader.result;
        
        if (selectedRecipients.length > 0 && recipientType) {
             let successCount = 0;
             const currentMonthYear = new Date().toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
             
             const recipientsToProcess = selectedRecipients.filter(id => 
                id !== ALL_CUSTOMERS_VALUE && 
                id !== ALL_SUPPLIERS_VALUE && 
                id !== ALL_ACCOUNTANTS_VALUE && 
                id !== ALL_SERVICE_STATIONS_VALUE && 
                id !== ALL_EMPLOYEES_VALUE
             );

             for (const recipientId of recipientsToProcess) {
                 try {
                     let folderId = null;
                     
                     if (createMonthFolder) {
                         let stationId = null;
                         let empId = null;
                         const folderOptions = {};
                         
                         if (recipientType === 'employee') empId = recipientId;
                         else if (recipientType === 'service_station') stationId = recipientId;
                         else if (recipientType === 'customer') folderOptions.customerId = recipientId;
                         else if (recipientType === 'supplier') folderOptions.supplierId = recipientId;
                         else if (recipientType === 'accountant') folderOptions.accountantId = recipientId;
                         
                         const foldersResponse = await Api.getFolders(selectedCompanyIdForCreation, null, stationId, empId, 'geral', folderOptions);
                         const folders = Array.isArray(foldersResponse) ? foldersResponse : (foldersResponse.data || []);
                         const existingFolder = folders.find(f => f.name === currentMonthYear);
                         
                         if (existingFolder) {
                             folderId = existingFolder.id;
                         } else {
                             const folderPayload = {
                                 name: currentMonthYear,
                                 company_id: selectedCompanyIdForCreation,
                                 parent_id: null,
                                 category: 'geral'
                             };
                             
                             if (recipientType === 'employee') folderPayload.employee_id = recipientId;
                             else if (recipientType === 'service_station') folderPayload.service_station_id = recipientId;
                             else if (recipientType === 'customer') folderPayload.customer_id = recipientId;
                             else if (recipientType === 'supplier') folderPayload.supplier_id = recipientId;
                             else if (recipientType === 'accountant') folderPayload.accountant_id = recipientId;
                             
                             const newFolderResponse = await Api.createFolder(folderPayload);
                             folderId = newFolderResponse.id || newFolderResponse.data?.id;
                             
                             if (!folderId) {
                                 const retryFoldersResponse = await Api.getFolders(selectedCompanyIdForCreation, null, stationId, empId, 'geral', folderOptions);
                                 const retryFolders = Array.isArray(retryFoldersResponse) ? retryFoldersResponse : (retryFoldersResponse.data || []);
                                 const createdFolder = retryFolders.find(f => f.name === currentMonthYear);
                                 if (createdFolder) folderId = createdFolder.id;
                             }
                         }
                     }
                     
                     const payload = {
                          name: uploadFile.name,
                          folder_id: folderId || (currentFolder ? currentFolder.id : null),
                          company_id: selectedCompanyIdForCreation,
                          file_data: base64,
                          file_type: uploadFile.type,
                          file_size: uploadFile.size,
                          document_type: documentType
                     };
                     
                     if (recipientType === 'employee') payload.employee_id = recipientId;
                     else if (recipientType === 'service_station') payload.service_station_id = recipientId;
                     else if (recipientType === 'customer') payload.customer_id = recipientId;
                     else if (recipientType === 'supplier') payload.supplier_id = recipientId;
                     else if (recipientType === 'accountant') payload.accountant_id = recipientId;

                     await Api.createFile(payload);
                     successCount++;
                 } catch (err) {
                     console.error(`Erro ao enviar para ${recipientId}`, err);
                 }
             }
             
             if (successCount > 0) {
                 toast.success(`${successCount} arquivos enviados com sucesso!`);
             } else {
                 toast.error('Falha ao enviar arquivos.');
             }
        } else {
            await Api.createFile({
              name: uploadFile.name,
              folder_id: currentFolder ? currentFolder.id : null,
              company_id: selectedCompanyIdForCreation,
              file_data: base64,
              file_type: uploadFile.type,
              file_size: uploadFile.size,
              document_type: documentType
            });
            toast.success('Arquivo enviado com sucesso!');
        }

        setIsUploadModalOpen(false);
        setUploadFile(null);
        setDocumentType('Arquivo Livre');
        setRecipientType('');
        setSelectedRecipients([]);
        setCreateMonthFolder(false);
        loadContent();
        setIsSubmitting(false);
      };
      reader.onerror = () => {
        toast.error('Erro ao ler arquivo.');
        setIsSubmitting(false);
      };
    } catch (error) {
      toast.error('Erro ao enviar arquivo.');
      setIsSubmitting(false);
    }
  }

  function confirmDelete(item, type) {
    setItemToDelete({ ...item, type });
    setIsDeleteModalOpen(true);
  }

  async function handleDelete() {
    if (!itemToDelete) return;
    setIsSubmitting(true);
    try {
      if (itemToDelete.type === 'folder') {
        await Api.deleteFolder(itemToDelete.id);
      } else {
        await Api.deleteFile(itemToDelete.id);
      }
      toast.success('Item excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      loadContent();
    } catch (error) {
      toast.error('Erro ao excluir item.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDownload(file) {
    try {
      const fullFile = await Api.getFile(file.id);
      if (fullFile && fullFile.file_data) {
        const link = document.createElement('a');
        link.href = fullFile.file_data;
        link.download = fullFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('Dados do arquivo não encontrados.');
      }
    } catch (error) {
      toast.error('Erro ao baixar arquivo.');
    }
  }

  async function handlePreview(file) {
    try {
      const fullFile = await Api.getFile(file.id);
      if (fullFile && fullFile.file_data) {
        // Handle Base64 Data URI
        const fileData = fullFile.file_data;
        
        // If it's a direct data URI, we can try to convert to blob for better handling
        // or just open it. Window.open with data URI is often blocked.
        // Let's convert to Blob.
        
        let base64 = fileData;
        let contentType = fullFile.file_type || 'application/octet-stream';

        if (fileData.includes(',')) {
            const parts = fileData.split(',');
            // parts[0] is "data:image/png;base64"
            // parts[1] is the base64 string
            base64 = parts[1];
        }

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        const url = URL.createObjectURL(blob);
        
        window.open(url, '_blank');
        
        // Cleanup after a delay (optional, but good practice if not needed immediately, 
        // though for window.open we need it to persist for a bit. 
        // Browsers handle blob URL lifetime usually until document unload, 
        // but explicit revoke is better if we could track it. 
        // For now, let's leave it.)
      } else {
        toast.error('Dados do arquivo não encontrados.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao visualizar arquivo.');
    }
  }



  // Prepare Data for Table
  const filterEntityOptions = useMemo(() => {
    const opts = [{ value: 'all', label: 'TODOS' }];
    
    const showCustomers = filterEntityType === 'all' || filterEntityType === 'customer';
    const showSuppliers = filterEntityType === 'all' || filterEntityType === 'supplier';
    const showAccountants = filterEntityType === 'all' || filterEntityType === 'accountant';
    const showServiceStations = filterEntityType === 'all' || filterEntityType === 'service_station';
    const showEmployees = filterEntityType === 'all' || filterEntityType === 'employee';

    if (showCustomers && customers.length > 0) {
        if (filterEntityType === 'all') opts.push({ label: '--- Clientes ---', value: 'disabled_c', isDisabled: true });
        customers.forEach(c => opts.push({ value: `c_${c.id}`, label: c.name || 'Sem Nome' }));
    }
    
    if (showSuppliers && suppliers.length > 0) {
        if (filterEntityType === 'all') opts.push({ label: '--- Fornecedores ---', value: 'disabled_s', isDisabled: true });
        suppliers.forEach(s => opts.push({ value: `s_${s.id}`, label: s.name || s.trade_name || 'Sem Nome' }));
    }

    if (showAccountants && accountants.length > 0) {
        if (filterEntityType === 'all') opts.push({ label: '--- Contadores ---', value: 'disabled_a', isDisabled: true });
        accountants.forEach(a => opts.push({ value: `a_${a.id}`, label: a.name || a.trade_name || 'Sem Nome' }));
    }
    
    if (showServiceStations && serviceStations.length > 0) {
        if (filterEntityType === 'all') opts.push({ label: '--- Postos ---', value: 'disabled_ss', isDisabled: true });
        serviceStations.forEach(s => opts.push({ value: `ss_${s.id}`, label: s.name || 'Sem Nome' }));
    }

    if (showEmployees && employees.length > 0) {
        if (filterEntityType === 'all') opts.push({ label: '--- Funcionários ---', value: 'disabled_e', isDisabled: true });
        employees.forEach(e => opts.push({ value: `e_${e.id}`, label: e.name || 'Sem Nome' }));
    }
    
    return opts;
  }, [customers, suppliers, accountants, serviceStations, employees, filterEntityType]);

  const fileTypeOptions = useMemo(() => {
    const types = new Set(files.map(f => f.file_type));
    return [{value: 'all', label: 'TODOS'}, ...Array.from(types).map(t => ({value: t, label: t}))];
  }, [files]);

  const filteredItems = useMemo(() => {
    let items = [
      ...folders.map(f => ({ ...f, type: 'folder' })),
      ...files.map(f => ({ ...f, type: 'file' }))
    ];
    
    // 1. Search
    if (searchTerm) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 2. Filter Entity Type
    if (filterEntityType !== 'all') {
      items = items.filter(item => {
        if (item.type !== 'folder') return false;
        
        if (filterEntityType === 'customer') return item.customers && item.customers.length > 0;
        if (filterEntityType === 'supplier') return !!item.supplier;
        if (filterEntityType === 'accountant') return !!item.accountant;
        if (filterEntityType === 'service_station') return !!item.serviceStation;
        if (filterEntityType === 'employee') return !!item.employee;
        
        return false;
      });
    }

    // 3. Filter Entity
    if (filterEntity !== 'all') {
      items = items.filter(item => {
        if (item.type !== 'folder') return false;
        
        const [type, idStr] = filterEntity.split('_');
        const id = Number(idStr);

        if (type === 'c') {
            return item.customers && item.customers.some(c => c.id === id);
        } else if (type === 's') {
            return item.supplier && item.supplier.id === id;
        } else if (type === 'a') {
            return item.accountant && item.accountant.id === id;
        } else if (type === 'ss') {
            return item.serviceStation && item.serviceStation.id === id;
        } else if (type === 'e') {
            return item.employee && item.employee.id === id;
        }
        return false;
      });
    }

    // 4. Filter File Type
    if (filterType !== 'all') {
      items = items.filter(item => {
        if (item.type === 'file') {
          return item.file_type === filterType;
        }
        return false;
      });
    }

    // 5. Sort
    items.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      switch (sortOrder) {
        case 'name_asc':
          return nameA.localeCompare(nameB);
        case 'name_desc':
          return nameB.localeCompare(nameA);
        case 'date_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'date_desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return items;
  }, [folders, files, searchTerm, filterEntity, filterType, sortOrder, filterEntityType]);

  // Drag and Drop Handlers
  function handleDragStart(e, item) {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: item.type, id: item.id }));
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, targetId = null) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId) {
      setDragTargetId(targetId);
    }
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragTargetId(null);
  }

  async function handleDrop(e, targetFolder) {
    e.preventDefault();
    setDragTargetId(null);
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    try {
      const draggedItem = JSON.parse(data);
      
      // Prevent dropping into self
      if (draggedItem.type === 'folder' && draggedItem.id === targetFolder.id) return;
      
      // Perform move
      if (draggedItem.type === 'folder') {
        await Api.updateFolder(draggedItem.id, { parent_id: targetFolder.id });
      } else {
        await Api.updateFile(draggedItem.id, { folder_id: targetFolder.id });
      }
      
      toast.success('Item movido com sucesso!');
      loadContent();
    } catch (error) {
      console.error('Drop error:', error);
      toast.error('Erro ao mover item.');
    }
  }

  async function handleDropOnBreadcrumb(e, targetFolder) {
    e.preventDefault();
    setDragTargetId(null);
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    try {
      const draggedItem = JSON.parse(data);
      const targetId = targetFolder ? targetFolder.id : null;

      // Prevent redundant moves
      if (draggedItem.type === 'folder' && draggedItem.id === targetId) return;

      if (draggedItem.type === 'folder') {
        await Api.updateFolder(draggedItem.id, { parent_id: targetId });
      } else {
        await Api.updateFile(draggedItem.id, { folder_id: targetId });
      }

      toast.success('Item movido com sucesso!');
      loadContent();
    } catch (error) {
      console.error('Drop error:', error);
      toast.error('Erro ao mover item.');
    }
  }

  function formatBytes(bytes, decimals = 2) {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage>
      <div className="manager-header">
        <div className="manager-header-title">
          <div className="manager-header-icon">
            <FiFolder size={20} />
          </div>
          <div>
            <h1>Arquivos</h1>
            <p>Gerencie pastas e documentos da empresa</p>
          </div>
        </div>
        <div className="manager-actions">
          {permissions.canCreate && (
            <Button
              onClick={() => {
                setNewFolderName('');
                setLinkType('internal');
                setSelectedCustomers([]);
                setSelectedSuppliers([]);
                setSelectedAccountants([]);
                
                if (currentFolder && currentFolder.company_id) {
                  setSelectedCompanyIdForCreation(String(currentFolder.company_id));
                } else {
                  setSelectedCompanyIdForCreation('');
                }
                
                setIsFolderModalOpen(true);
              }}
              startIcon={<FiFolderPlus />}
            >
              Nova Pasta
            </Button>
          )}&nbsp;
          {permissions.canCreate && (
            <div className="file-manager-dropdown-container" ref={deliveryDropdownRef}>
              <Button
                onClick={() => setIsDeliveryDropdownOpen(!isDeliveryDropdownOpen)}
                variant="outline"
                startIcon={<FiUpload />}
                endIcon={<FiChevronDown />}
              >
                Entrega
              </Button>
              
              {isDeliveryDropdownOpen && (
                <div className="file-manager-dropdown-menu">
                  <button 
                    className="file-manager-dropdown-item"
                    onClick={() => {
                      setIsDeliveryDropdownOpen(false);
                      setUploadFile(null);
                      
                      if (currentFolder && currentFolder.company_id) {
                        setSelectedCompanyIdForCreation(String(currentFolder.company_id));
                      } else {
                        setSelectedCompanyIdForCreation('');
                      }

                      setIsUploadModalOpen(true);
                    }}
                  >
                    <FiFile size={16} />
                    Arquivo Livre
                  </button>
                  <button 
                    className="file-manager-dropdown-item"
                    onClick={() => {
                      setIsDeliveryDropdownOpen(false);
                      setIsSpecificDocumentModalOpen(true);
                    }}
                  >
                    <FiBriefcase size={16} />
                    Documento&nbsp;Específico
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Card className="manager-filters-card">
        <div className="manager-filters-header">
          <div className="manager-filters-title">
            <FiFilter size={16} />
            <span>Filtros</span>
          </div>
          {folders.length + files.length > 0 && (
            <span className="manager-filters-count">
              {folders.length} de {folders.length + files.length} pastas e arquivos
            </span>
          )}
        </div>
        <div className="manager-filters-grid-custom">
           <div className="manager-filter-group">
              <MultiSelect
                id="filter-company-files"
                label="Empresas"
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...companies.map(c => ({ 
                    value: String(c.id), 
                    label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}` 
                  }))
                ]}
                value={companyFilterIds}
                onChange={handleCompanyFilterChange}
                placeholder="Selecione empresas"
                forceHeight={'46px'}
              />
           </div>
           <div className="manager-filter-group">
              <label className="manager-filter-label">Buscar</label>
              <Input
                placeholder="Buscar por nome..."
                icon={FiSearch}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
           </div>
           <div className="manager-filter-group">
              <label className="manager-filter-label">Tipo Vínculo</label>
              <Select
                options={[
                  { value: 'all', label: 'TODOS' },
                  { value: 'customer', label: 'Clientes' },
                  { value: 'supplier', label: 'Fornecedores' },
                  { value: 'accountant', label: 'Contadores' },
                  { value: 'service_station', label: 'Postos' },
                  { value: 'employee', label: 'Funcionários' }
                ]}
                value={filterEntityType}
                onChange={(e) => {
                  setFilterEntityType(e.target.value);
                  setFilterEntity('all');
                }}
              />
           </div>
           <div className="manager-filter-group">
              <label className="manager-filter-label">Entidade</label>
              <Select
                options={filterEntityOptions}
                value={filterEntity}
                searchable
                onChange={(e) => setFilterEntity(e.target.value)}
              />
           </div>
           <div className="manager-filter-group">
              <label className="manager-filter-label">Tipo Arquivo</label>
              <Select
                options={fileTypeOptions}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              />
           </div>
           {/* <div className="manager-filter-group">
              <label className="manager-filter-label">Ordenar por</label>
              <Select
                options={[
                  { value: 'name_asc', label: 'Nome (A-Z)' },
                  { value: 'name_desc', label: 'Nome (Z-A)' },
                  { value: 'date_asc', label: 'Data (Antigos primeiro)' },
                  { value: 'date_desc', label: 'Data (Novos primeiro)' }
                ]}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
           </div> */}
        </div>
      </Card>

      {/* Breadcrumbs Area */}
      <div className="breadcrumbs-container" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
        <Button 
          variant="ghost" 
          size="sm" 
          startIcon={<FiHome size={16} />} 
          onClick={() => handleNavigateBreadcrumb(-1)}
          disabled={!currentFolder}
          onDragOver={(e) => handleDragOver(e, 'root')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDropOnBreadcrumb(e, null)}
          className={dragTargetId === 'root' ? 'drag-over-active' : ''}
        />
        {breadcrumbs.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <FiArrowLeft style={{ transform: 'rotate(180deg)', width: '12px' }} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigateBreadcrumb(index)}
              className={`${index === breadcrumbs.length - 1 ? 'text-primary font-bold' : ''} ${dragTargetId === folder.id ? 'drag-over-active' : ''}`}
              onDragOver={(e) => handleDragOver(e, folder.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDropOnBreadcrumb(e, folder)}
            >
              {folder.name}
            </Button>
          </React.Fragment>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div style={{display: 'flex', justifyContent: 'center'}}>
          <div className="empty-state" style={{display: 'block !important'}}>
            <center><div className="empty-state-icon" style={{display: 'flex', justifyContent: 'center', height: '100px', width: '100px'}}>
              <FiFolder style={{margin: '0px',}} />
            </div></center>
            <h3>{currentFolder ? "Esta pasta está vazia" : "Nenhum arquivo encontrado"}</h3>
            <p>{currentFolder ? "Faça upload de arquivos ou crie novas pastas." : "Crie uma nova pasta ou faça upload de arquivos para começar."}</p>
          </div>
        </div>
      ) : (
        <div className="file-grid">
          {filteredItems.map(item => (
            <div 
              key={`${item.type}-${item.id}`}
              className={`file-card ${item.type === 'folder' && dragTargetId === item.id ? 'drag-over-active' : ''}`}
              onClick={() => item.type === 'folder' && handleNavigate(item)}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={item.type === 'folder' ? (e) => handleDragOver(e, item.id) : undefined}
              onDragLeave={item.type === 'folder' ? handleDragLeave : undefined}
              onDrop={item.type === 'folder' ? (e) => handleDrop(e, item) : undefined}
            >
              <div className="file-card-actions" onClick={e => e.stopPropagation()}>
                 {item.type === 'file' && (
                    <>
                      <button 
                        className="file-card-action-btn"
                        onClick={() => handlePreview(item)}
                        title="Visualizar"
                      >
                        <FiEye size={14} />
                      </button>
                      <button 
                        className="file-card-action-btn"
                        onClick={() => handleDownload(item)}
                        title="Baixar"
                      >
                        <FiDownload size={14} />
                      </button>
                    </>
                 )}
                 {permissions.canDelete && (
                   <button 
                     className="file-card-action-btn delete"
                     onClick={() => confirmDelete(item, item.type)}
                     title="Excluir"
                   >
                     <FiTrash2 size={14} />
                   </button>
                 )}
                 {permissions.canEdit && (
                   <button 
                    className="file-card-action-btn"
                    onClick={() => openRenameModal(item)}
                    title="Renomear"
                  >
                    <IconEdit size={14} />
                  </button>
                 )}
              </div>

              <div className={`file-card-icon ${item.type}`}>
                {item.type === 'folder' ? <FiFolder /> : <FiFile />}
              </div>

              {item.type === 'folder' && (
                  <div style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      fontSize: '0.65rem',
                      fontWeight: '700',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: (item.customers && item.customers.length > 0) ? '#eff6ff' : 
                                       (item.supplier) ? '#ecfdf5' : 
                                       (item.accountant) ? '#f5f3ff' : 
                                       (item.serviceStation) ? '#fff7ed' :
                                       (item.employee) ? '#f0f9ff' : '#f1f5f9',
                      color: (item.customers && item.customers.length > 0) ? '#3b82f6' : 
                             (item.supplier) ? '#10b981' : 
                             (item.accountant) ? '#8b5cf6' : 
                             (item.serviceStation) ? '#f97316' :
                             (item.employee) ? '#0ea5e9' : '#64748b',
                      textTransform: 'uppercase',
                      opacity: 0.9
                  }}>
                      {(item.customers && item.customers.length > 0) ? 'Cliente' : 
                       (item.supplier) ? 'Fornecedor' : 
                       (item.accountant) ? 'Contador' : 
                       (item.serviceStation) ? 'Posto' : 
                       (item.employee) ? 'Funcionário' : 'Interno'}
                  </div>
              )}

              {item.type === 'file' && item.signature_required && (
                  <div style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      fontSize: '0.65rem',
                      fontWeight: '700',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: item.is_signed ? '#ecfdf5' : '#fef2f2',
                      color: item.is_signed ? '#10b981' : '#ef4444',
                      textTransform: 'uppercase',
                      opacity: 0.9,
                      border: `1px solid ${item.is_signed ? '#10b981' : '#ef4444'}`
                  }}>
                      {item.is_signed ? 'Assinado' : 'Não Assinado'}
                  </div>
              )}
              
              <div className="file-card-name" title={item.name}>
                {item.name}
              </div>
              
              <div className="file-card-meta">
                <span style={{ display: 'block', width: '100%', marginBottom: '2px', fontWeight: 'bold', color: '#475569' }}>
                   {(() => {
                       const c = item.company || companies.find(comp => String(comp.id) === String(item.company_id));
                       return c ? (c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`) : '';
                   })()}
                </span>
                {item.customers && item.customers.length > 0 && (
                  <span>
                    <FiBriefcase size={10} /> 
                    {item.customers.map(c => c.name).join(', ')}
                  </span>
                )}
                {item.supplier && (
                  <span>
                    <FiBriefcase size={10} /> 
                    {item.supplier.name || item.supplier.trade_name}
                  </span>
                )}
                {item.accountant && (
                  <span>
                    <FiBriefcase size={10} /> 
                    {item.accountant.name || item.accountant.trade_name}
                  </span>
                )}
                {item.serviceStation && (
                  <span>
                    <FiMapPin size={10} /> 
                    {item.serviceStation.name}
                  </span>
                )}
                {item.employee && (
                  <span>
                    <FiUser size={10} /> 
                    {item.employee.name}
                  </span>
                )}
                <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                {item.type === 'file' && <span>{formatBytes(item.file_size)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Folder Modal */}
      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        title="Nova Pasta"
        width="600px"
      >
      <div className='form-component'>
        <div className="form-body">
          <div className="form-row">
            <div className="form-group">
               <Select
                  label={"Empresa"}
                  options={[{ value: '', label: 'SELECIONE UMA EMPRESA...' }, ...companies.map(c => ({ value: String(c.id), label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}` }))]}
                  value={selectedCompanyIdForCreation}
                  onChange={(e) => setSelectedCompanyIdForCreation(e.target.value)}
                  searchable
                  disabled={!!currentFolder}
               />
            </div>
            <div className="form-group" style={{marginTop: '1rem'}}>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Ex: Contratos 2024"
                label={"Nome da Pasta"}
                autoFocus
              />
            </div>

            <div className="form-group" style={{marginTop: '1rem'}}>
               <Select
                  label={"Tipo de Pasta"}
                  options={[
                      { value: 'internal', label: 'Interna (Privada)' },
                      { value: 'customer', label: 'Vinculada a Cliente' },
                      { value: 'supplier', label: 'Vinculada a Fornecedor' },
                      { value: 'accountant', label: 'Vinculada a Contador' },
                      { value: 'service_station', label: 'Vinculada a Posto' },
                      { value: 'employee', label: 'Vinculada a Funcionário' }
                  ]}
                  value={linkType}
                  onChange={(e) => setLinkType(e.target.value)}
               />
            </div>

            {linkType === 'internal' && (
                <div style={{ 
                    marginTop: '1rem', 
                    padding: '0.75rem', 
                    backgroundColor: '#fffbeb', 
                    border: '1px solid #fcd34d', 
                    borderRadius: '6px', 
                    color: '#92400e', 
                    fontSize: '0.9rem' 
                }}>
                    <strong>Atenção:</strong> Esta pasta será considerada como uma pasta interna privada.
                </div>
            )}

            {linkType === 'customer' && (
              <div className="form-group" style={{marginTop: '1rem'}}>
                <MultiSelect
                  label={"Selecione os Clientes:"}
                  options={customerOptions}
                  value={selectedCustomers}
                  onChange={handleCustomerSelectChange}
                  placeholder="Selecione os clientes..."
                />
              </div>
            )}

            {linkType === 'supplier' && (
              <div className="form-group" style={{marginTop: '1rem'}}>
                <MultiSelect
                  label={"Selecione os Fornecedores:"}
                  options={supplierOptions}
                  value={selectedSuppliers}
                  onChange={handleSupplierSelectChange}
                  placeholder="Selecione os fornecedores..."
                />
              </div>
            )}

            {linkType === 'accountant' && (
              <div className="form-group" style={{marginTop: '1rem'}}>
                <MultiSelect
                  label={"Selecione os Contadores:"}
                  options={accountantOptions}
                  value={selectedAccountants}
                  onChange={handleAccountantSelectChange}
                  placeholder="Selecione os contadores..."
                />
              </div>
            )}

            {linkType === 'service_station' && (
              <div className="form-group" style={{marginTop: '1rem'}}>
                <MultiSelect
                  label={"Selecione os Postos:"}
                  options={serviceStationOptions}
                  value={selectedServiceStations}
                  onChange={handleServiceStationSelectChange}
                  placeholder="Selecione os postos..."
                />
              </div>
            )}

            {linkType === 'employee' && (
              <div className="form-group" style={{marginTop: '1rem'}}>
                <MultiSelect
                  label={"Selecione os Funcionários:"}
                  options={employeeOptions}
                  value={selectedEmployees}
                  onChange={handleEmployeeSelectChange}
                  placeholder="Selecione os funcionários..."
                />
              </div>
            )}
          </div>
        </div>
        <div className="form-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsFolderModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateFolder} loading={isSubmitting}>Salvar</Button>
        </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload de Arquivo"
        width="600px"
      >
        <div className="form-body">
          <div className="form-grid form-component">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
               <Select
                  label={"Empresa"}
                  options={[{ value: '', label: 'SELECIONE UMA EMPRESA...' }, ...companies.map(c => ({ value: String(c.id), label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}` }))]}
                  value={selectedCompanyIdForCreation}
                  onChange={(e) => setSelectedCompanyIdForCreation(e.target.value)}
                  searchable
                  disabled={!!currentFolder}
               />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
               <Select
                  label={"Tipo de Documento"}
                   options={[
                       { value: 'Arquivo Livre', label: 'Arquivo Livre' },
                       { value: 'Arquivo Específico', label: 'Arquivo Específico' },
                       { value: '13º Salário', label: '13º Salário' },
                       { value: 'Contra Cheque', label: 'Contra Cheque' },
                       { value: 'Folha de Ponto', label: 'Folha de Ponto' }
                   ]}
                   value={documentType}
                   onChange={(e) => setDocumentType(e.target.value)}
                />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <Select
                    label="Tipo de Destinatário (Opcional)"
                    options={[
                        { value: '', label: 'SELECIONE...' },
                        { value: 'employee', label: 'Funcionário' },
                        { value: 'customer', label: 'Cliente' },
                        { value: 'supplier', label: 'Fornecedor' },
                        { value: 'accountant', label: 'Contador' },
                        { value: 'service_station', label: 'Posto' }
                    ]}
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value)}
                    disabled={!selectedCompanyIdForCreation}
                />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                        <MultiSelect
                          label={"Destinatários"}
                          options={uploadRecipientOptions}
                          value={selectedRecipients}
                          onChange={handleUploadRecipientSelectChange}
                          disabled={!recipientType || uploadRecipientOptions.length === 0}
                          placeholder="Selecione os destinatários..."
                        />
                    </div>
                </div>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div 
                    className={`feature-toggle-container ${createMonthFolder ? 'active' : ''}`}
                    onClick={() => setCreateMonthFolder(!createMonthFolder)}
                    style={{ marginBottom: 0 }}
                  >
                    <div className="toggle-track">
                      <div className="toggle-thumb" />
                    </div>
                    <div className="toggle-content">
                      <div className="toggle-title">Organizar arquivos por Mês/Ano</div>
                      <div className="toggle-description">
                        Cria automaticamente a pasta Mês/Ano para o destinatário. Se a pasta já existir, os arquivos serão salvos nela.
                      </div>
                    </div>
                  </div>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Selecionar Arquivo</label>
              <div className="upload-area" style={{ 
                border: '2px dashed #cbd5e1', 
                borderRadius: '8px', 
                padding: '2rem', 
                textAlign: 'center',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }} onClick={() => document.getElementById('fileInput').click()}>
                 {uploadFile ? (
                   <div style={{ color: '#0f172a', fontWeight: '500' }}>{uploadFile.name}</div>
                 ) : (
                   <div style={{ color: '#64748b' }}>
                     <FiUpload size={24} style={{ marginBottom: '0.5rem' }} /><br/>
                     Clique para selecionar
                   </div>
                 )}
                 <input 
                   id="fileInput"
                   type="file" 
                   style={{ display: 'none' }} 
                   onChange={(e) => setUploadFile(e.target.files[0])}
                 />
              </div>
            </div>
          </div>
        </div>
        <div className="form-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsUploadModalOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button onClick={handleUploadFile} loading={isSubmitting} disabled={!uploadFile}>Enviar</Button>
        </div>
      </Modal>

      <Modal
        isOpen={showUploadRecipientsModal}
        onClose={() => setShowUploadRecipientsModal(false)}
        title="Destinatários Selecionados"
        size="md"
      >
          <div className="form-body">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {selectedUploadRecipientsDetails.map(r => (
                      <div key={r.id} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                          <strong>{r.name || r.trade_name || r.corporate_name || r.razao_social || r.nome_fantasia}</strong>
                          <div style={{ fontSize: '12px', color: '#666' }}>{r.cpf || r.cnpj || r.cpf_cnpj}</div>
                      </div>
                  ))}
              </div>
          </div>
          <div className="modal-actions">
              <Button onClick={() => setShowUploadRecipientsModal(false)}>Fechar</Button>
          </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
        width="400px"
      >
        <div className="form-body">
          <p>Tem certeza que deseja excluir <strong>{itemToDelete?.name}</strong>?</p>
          {itemToDelete?.type === 'folder' && (
             <p style={{ color: '#ef4444', fontSize: '0.9rem' }}>Atenção: Todo o conteúdo desta pasta será apagado.</p>
          )}
        </div>
        <div className="form-actions">
          <Button 
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button onClick={handleDelete} loading={isSubmitting}>Excluir</Button>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        title="Renomear Item"
        width="500px"
      >
        <div className="form-body">
          <div className="form-row">
            <div className="form-group">
              <Input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                placeholder="Novo nome"
                label={"Nome"}
                autoFocus
              />
            </div>
          </div>
        </div>
        <div className="form-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsRenameModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleRename} loading={isSubmitting}>Salvar</Button>
        </div>
      </Modal>

      <SendSpecificDocumentModal
        isOpen={isSpecificDocumentModalOpen}
        onClose={() => setIsSpecificDocumentModalOpen(false)}
        companies={companies}
        onSuccess={() => loadContent()}
      />
    </PageComponent>
  );
}
